// ============================================================
// CASUAL MODE — preset level definitions
// ============================================================
//
// 簡易模式（Casual Mode）關卡資料。每個關卡定義：
//   - 雙方單位的「兵種 + 預擺位置」（玩家無法新增 / 移除 / 重新放置）
//   - 敵方單位的固定行走路徑
//   - 8×8 地形 grid（草地 / 積水）
//
// 玩家在 DEPLOY 階段唯一能做的就是「規劃我方單位的行走路徑」與
// 確認開戰；其餘 deployment UI（palette / save slot / 重新放置等）
// 在 casual 模式下會被 Game / Renderer / Input 端共同屏蔽。
//
// 戰鬥本身（MATCH3 + SCHED + EXEC）走完整單機流程，AI 用既有
// LocalServerAdapter；AI 兵種與路徑直接從本檔 level.ai 取，不走
// generateAiUnits 隨機放置。
//
// 新增關卡：在 CASUAL_LEVELS 陣列加一筆 CasualLevelDef 即可。
//
// 地形 grid 編碼：8 個字串，每字串 8 個字元（自上至下 y=0..7）：
//   '.' = none
//   'g' = grass        （弓兵打草地目標 -50%；Fire 點燃 → burningGrass）
//   'p' = puddle       （騎兵碰到 -1 步；Lightning +30%）
//
// 地形限制與既有 §2.8 相同：建議只放在 y ∈ [2,5] 中央 4 列（rows 0-1
// 是 AI 佈陣區、rows 6-7 是玩家佈陣區）。本檔不強制檢查，但若放在
// 玩家 / AI 佈陣行可能與單位位置衝突視覺上。
//
// 單位位置限制：
//   - player units 必須在 rows 6-7、避開玩家基地 (3,7) (4,7)
//   - ai     units 必須在 rows 0-1、避開敵方基地 (3,0) (4,0)
//   - 同隊不可堆疊（同 (x,y) 只能放 1 隻）
// ============================================================

import type { Pos } from '../BattleUnit.ts';
import type { TerrainGrid, TerrainCell } from '../server/terrain.ts';
import { BATTLE_COLS, BATTLE_ROWS } from '../constants.ts';

/** Unit type accepted in level data. Mirror of `keyof UCFG`. */
export type CasualUnitType = 'Infantry' | 'Cavalry' | 'Archer' | 'Mage';

/** One unit placement on a casual level. */
export interface CasualLevelUnit {
  type: CasualUnitType;
  pos: Pos;
  /**
   * 預設行走路徑。
   *   - AI 單位：通常會寫滿（劇情關卡需要可預期的敵軍動線）。
   *   - 玩家單位：通常留空 `[]`，讓玩家自己畫；如果關卡設計者想
   *     給玩家「預設路徑」當提示，也可以填，玩家進 DEPLOY 後仍可
   *     清掉重畫。
   */
  path: Pos[];
}

/** Full casual level definition. */
export interface CasualLevelDef {
  /** Stable id（localStorage 進度紀錄用；不要重複）。 */
  id: string;
  /** 章節列表顯示名稱。 */
  title: string;
  /** 章節列表顯示副標題。 */
  subtitle?: string;
  /** 玩家單位（team 0）— rows 6-7。 */
  player: CasualLevelUnit[];
  /** 敵方單位（team 1）— rows 0-1。 */
  ai: CasualLevelUnit[];
  /**
   * 地形 8×8 grid，以 8 字串編碼。每字串 8 char：
   *   '.' = none / 'g' = grass / 'p' = puddle
   * 索引：terrainRows[y].charAt(x)。
   */
  terrainRows: [string, string, string, string, string, string, string, string];
}

// ------------------------------------------------------------
// Helpers — convert the terrain string representation into a
// TerrainGrid that the server / renderer expects.
// ------------------------------------------------------------

/** Decode one char into a TerrainCell. Unknown chars treated as 'none'. */
function decodeTerrainChar(ch: string): TerrainCell {
  if (ch === 'g' || ch === 'G') return 'grass';
  if (ch === 'p' || ch === 'P') return 'puddle';
  return 'none';
}

/**
 * Build a fresh TerrainGrid (deep-cloned per call so callers can safely
 * mutate without leaking back into the const level data).
 *
 * Validates dimensions: 8 rows × 8 cols. Out-of-range / missing chars
 * silently default to 'none' to keep design-time mistakes recoverable.
 */
export function buildTerrainGrid(level: CasualLevelDef): TerrainGrid {
  const grid: TerrainGrid = [];
  for (let y = 0; y < BATTLE_ROWS; y++) {
    const row: TerrainCell[] = [];
    const src = level.terrainRows[y] || '';
    for (let x = 0; x < BATTLE_COLS; x++) {
      row.push(decodeTerrainChar(src.charAt(x)));
    }
    grid.push(row);
  }
  return grid;
}

// ------------------------------------------------------------
// CASUAL LEVELS — 5 preset levels
//
// 設計理念：難度遞進 + 兵種組合多樣，每關都有不同地形重點，
// 讓玩家逐步熟悉路徑規劃在不同情境下的決策。
// ------------------------------------------------------------

export const CASUAL_LEVELS: CasualLevelDef[] = [
  // ----------------------------------------------------------
  // Level 1 — 入門：純步兵對決
  // 沒有地形，玩家有兵力優勢，幾乎隨便畫路徑都能贏，
  // 主要是給玩家熟悉 casual mode 的 UI。
  // ----------------------------------------------------------
  {
    id: 'casual-1',
    title: '第 1 關 · 初試啼聲',
    subtitle: '純步兵小戰 — 規劃推進路線',
    player: [
      { type: 'Infantry', pos: { x: 1, y: 7 }, path: [] },
      { type: 'Infantry', pos: { x: 6, y: 7 }, path: [] },
      { type: 'Infantry', pos: { x: 2, y: 6 }, path: [] },
    ],
    ai: [
      { type: 'Infantry', pos: { x: 2, y: 0 }, path: [
        { x: 2, y: 1 }, { x: 2, y: 2 }, { x: 2, y: 3 }, { x: 2, y: 4 },
      ] },
      { type: 'Infantry', pos: { x: 5, y: 0 }, path: [
        { x: 5, y: 1 }, { x: 5, y: 2 }, { x: 5, y: 3 }, { x: 5, y: 4 },
      ] },
    ],
    terrainRows: [
      '........',
      '........',
      '........',
      '........',
      '........',
      '........',
      '........',
      '........',
    ],
  },

  // ----------------------------------------------------------
  // Level 2 — 騎兵衝鋒：第一次接觸積水地形
  // 兩塊積水卡在中央，玩家若把騎兵直直開過去會被減速；
  // 練習「繞開積水」或「乾脆走另一條路」。
  // ----------------------------------------------------------
  {
    id: 'casual-2',
    title: '第 2 關 · 騎兵與積水',
    subtitle: '兩塊積水擋路 — 騎兵碰到會減速',
    player: [
      { type: 'Cavalry', pos: { x: 3, y: 6 }, path: [] },
      { type: 'Infantry', pos: { x: 1, y: 7 }, path: [] },
      { type: 'Infantry', pos: { x: 6, y: 7 }, path: [] },
    ],
    ai: [
      { type: 'Infantry', pos: { x: 1, y: 0 }, path: [
        { x: 1, y: 1 }, { x: 1, y: 2 }, { x: 1, y: 3 }, { x: 1, y: 4 }, { x: 1, y: 5 },
      ] },
      { type: 'Infantry', pos: { x: 6, y: 0 }, path: [
        { x: 6, y: 1 }, { x: 6, y: 2 }, { x: 6, y: 3 }, { x: 6, y: 4 }, { x: 6, y: 5 },
      ] },
      { type: 'Infantry', pos: { x: 4, y: 1 }, path: [
        { x: 4, y: 2 }, { x: 4, y: 3 }, { x: 4, y: 4 },
      ] },
    ],
    terrainRows: [
      '........',
      '........',
      '...pp...',
      '...pp...',
      '........',
      '........',
      '........',
      '........',
    ],
  },

  // ----------------------------------------------------------
  // Level 3 — 弓兵與草地：學會用草地當掩體
  // 中央橫向草帶；把己方弓兵的目標放在草地外、自己單位停在草地上，
  // 可享受 50% 減傷 cover。同時 Fire 也可以點燃草地造成 DOT。
  // ----------------------------------------------------------
  {
    id: 'casual-3',
    title: '第 3 關 · 草地掩體',
    subtitle: '弓兵互射 — 草地可以擋傷',
    player: [
      { type: 'Archer', pos: { x: 1, y: 7 }, path: [] },
      { type: 'Archer', pos: { x: 6, y: 7 }, path: [] },
      { type: 'Infantry', pos: { x: 2, y: 6 }, path: [] },
      { type: 'Infantry', pos: { x: 5, y: 6 }, path: [] },
    ],
    ai: [
      { type: 'Archer', pos: { x: 1, y: 0 }, path: [
        { x: 1, y: 1 }, { x: 1, y: 2 }, { x: 1, y: 3 },
      ] },
      { type: 'Archer', pos: { x: 6, y: 0 }, path: [
        { x: 6, y: 1 }, { x: 6, y: 2 }, { x: 6, y: 3 },
      ] },
      { type: 'Infantry', pos: { x: 3, y: 1 }, path: [
        { x: 3, y: 2 }, { x: 3, y: 3 }, { x: 3, y: 4 }, { x: 3, y: 5 },
      ] },
      { type: 'Infantry', pos: { x: 4, y: 1 }, path: [
        { x: 4, y: 2 }, { x: 4, y: 3 }, { x: 4, y: 4 }, { x: 4, y: 5 },
      ] },
    ],
    terrainRows: [
      '........',
      '........',
      '........',
      '.gggggg.',
      '........',
      '........',
      '........',
      '........',
    ],
  },

  // ----------------------------------------------------------
  // Level 4 — 混合地形：玩家有完整三兵種
  // 草地 + 積水混合，玩家要替每個兵種畫不同性質的路線：
  // 騎兵繞開積水、步兵正面碾壓、弓兵躲草地後 cover 輸出。
  // ----------------------------------------------------------
  {
    id: 'casual-4',
    title: '第 4 關 · 多線推進',
    subtitle: '草地 + 積水混合地形',
    player: [
      { type: 'Infantry', pos: { x: 0, y: 7 }, path: [] },
      { type: 'Infantry', pos: { x: 7, y: 7 }, path: [] },
      { type: 'Cavalry', pos: { x: 3, y: 6 }, path: [] },
      { type: 'Archer', pos: { x: 1, y: 6 }, path: [] },
      { type: 'Archer', pos: { x: 6, y: 6 }, path: [] },
    ],
    ai: [
      { type: 'Infantry', pos: { x: 0, y: 0 }, path: [
        { x: 0, y: 1 }, { x: 0, y: 2 }, { x: 0, y: 3 }, { x: 0, y: 4 }, { x: 0, y: 5 },
      ] },
      { type: 'Infantry', pos: { x: 7, y: 0 }, path: [
        { x: 7, y: 1 }, { x: 7, y: 2 }, { x: 7, y: 3 }, { x: 7, y: 4 }, { x: 7, y: 5 },
      ] },
      { type: 'Archer', pos: { x: 2, y: 1 }, path: [
        { x: 2, y: 2 }, { x: 2, y: 3 },
      ] },
      { type: 'Archer', pos: { x: 5, y: 1 }, path: [
        { x: 5, y: 2 }, { x: 5, y: 3 },
      ] },
      { type: 'Infantry', pos: { x: 4, y: 0 }, path: [
        { x: 4, y: 1 }, { x: 4, y: 2 }, { x: 4, y: 3 }, { x: 4, y: 4 },
      ] },
    ],
    terrainRows: [
      '........',
      '........',
      '..gg....',
      '..gg.pp.',
      '.pp..gg.',
      '....gg..',
      '........',
      '........',
    ],
  },

  // ----------------------------------------------------------
  // Level 5 — 最終一戰：玩家滿編 vs 敵方滿編
  // 兵力相當、地形複雜。考驗玩家對所有機制的綜合運用。
  // ----------------------------------------------------------
  {
    id: 'casual-5',
    title: '第 5 關 · 決戰時刻',
    subtitle: '滿編對決 — 路徑規劃決定勝負',
    player: [
      { type: 'Infantry', pos: { x: 0, y: 7 }, path: [] },
      { type: 'Infantry', pos: { x: 7, y: 7 }, path: [] },
      { type: 'Infantry', pos: { x: 2, y: 7 }, path: [] },
      { type: 'Infantry', pos: { x: 5, y: 7 }, path: [] },
      { type: 'Cavalry', pos: { x: 3, y: 6 }, path: [] },
      { type: 'Cavalry', pos: { x: 4, y: 6 }, path: [] },
      { type: 'Archer', pos: { x: 1, y: 6 }, path: [] },
      { type: 'Archer', pos: { x: 6, y: 6 }, path: [] },
    ],
    ai: [
      { type: 'Infantry', pos: { x: 0, y: 0 }, path: [
        { x: 0, y: 1 }, { x: 0, y: 2 }, { x: 0, y: 3 }, { x: 0, y: 4 }, { x: 0, y: 5 }, { x: 0, y: 6 },
      ] },
      { type: 'Infantry', pos: { x: 7, y: 0 }, path: [
        { x: 7, y: 1 }, { x: 7, y: 2 }, { x: 7, y: 3 }, { x: 7, y: 4 }, { x: 7, y: 5 }, { x: 7, y: 6 },
      ] },
      { type: 'Infantry', pos: { x: 2, y: 0 }, path: [
        { x: 2, y: 1 }, { x: 2, y: 2 }, { x: 2, y: 3 }, { x: 2, y: 4 },
      ] },
      { type: 'Infantry', pos: { x: 5, y: 0 }, path: [
        { x: 5, y: 1 }, { x: 5, y: 2 }, { x: 5, y: 3 }, { x: 5, y: 4 },
      ] },
      { type: 'Cavalry', pos: { x: 3, y: 1 }, path: [
        { x: 3, y: 2 }, { x: 3, y: 3 }, { x: 3, y: 4 }, { x: 3, y: 5 },
      ] },
      { type: 'Archer', pos: { x: 1, y: 1 }, path: [
        { x: 1, y: 2 }, { x: 1, y: 3 },
      ] },
      { type: 'Archer', pos: { x: 6, y: 1 }, path: [
        { x: 6, y: 2 }, { x: 6, y: 3 },
      ] },
    ],
    terrainRows: [
      '........',
      '........',
      '.gg..pp.',
      '..g..p..',
      '..p..g..',
      '.pp..gg.',
      '........',
      '........',
    ],
  },
];

/** Look up a level by id. Returns null if id not registered. */
export function getCasualLevel(id: string): CasualLevelDef | null {
  return CASUAL_LEVELS.find((l) => l.id === id) || null;
}
